// ROOT: frontend/src/components/Pricing.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { INSTAGRAM_PLANS } from '../mock';
import {
  Check,
  ChevronRight,
  Sparkles,
  Users,
  TrendingUp,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { api } from '../api';

const TRUST_MESSAGES = [
  { icon: Users, text: 'Used by 35,000+ creators today' },
  { icon: TrendingUp, text: '4,812 new followers delivered this hour' },
  { icon: ShieldCheck, text: '100% money-back guarantee' },
  { icon: Zap, text: 'Setup in under 2 minutes' },
];

const TrustBar = ({ offset = 0 }) => {
  const [idx, setIdx] = useState(offset % TRUST_MESSAGES.length);

  useEffect(() => {
    const id = setInterval(
      () => setIdx((v) => (v + 1) % TRUST_MESSAGES.length),
      3200
    );

    return () => clearInterval(id);
  }, []);

  const { icon: Icon, text } = TRUST_MESSAGES[idx];

  return (
    <div className="mt-5 pt-4 border-t border-slate-100">
      <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>

        <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />

        <span key={idx} className="fade-in-up truncate">
          {text}
        </span>
      </div>
    </div>
  );
};

const Pricing = () => {
  const [brandName, setBrandName] = useState('IGGrowth');
  const [plans, setPlans] = useState(INSTAGRAM_PLANS);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await api.getPublicSettings();

        const name =
          settings?.brandName ||
          settings?.brand_name ||
          'IGGrowth';

        setBrandName(name);

        /*
         * Admin pricing format:
         *
         * Starter: 29
         * Pro: 55
         *
         * We keep the existing plan structure from mock.js
         * and only override the prices from Admin settings.
         */
        const pricingText =
          settings?.instagramPricing ||
          settings?.instagram_pricing ||
          '';

        if (pricingText.trim()) {
          const priceMap = {};

          pricingText.split('\n').forEach((line) => {
            const parts = line.split(':');

            if (parts.length >= 2) {
              const planName = parts[0].trim().toLowerCase();
              const price = Number(
                parts.slice(1).join(':').replace(/[^0-9.]/g, '')
              );

              if (planName && Number.isFinite(price) && price > 0) {
                priceMap[planName] = price;
              }
            }
          });

          if (Object.keys(priceMap).length > 0) {
            setPlans((currentPlans) =>
              currentPlans.map((plan) => {
                const adminPrice = priceMap[plan.name.toLowerCase()];

                return adminPrice
                  ? {
                      ...plan,
                      price: adminPrice,
                    }
                  : plan;
              })
            );
          }
        }
      } catch (error) {
        console.error('Failed to load pricing settings:', error);
      }
    };

    loadSettings();
  }, []);

  return (
    <section id="pricing" className="py-24">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">

        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-sm font-bold tracking-widest uppercase plixy-gradient-text">
            Instagram Pricing
          </p>

          <h2 className="mt-3 text-4xl md:text-5xl font-extrabold text-[#0a1930]">
            {brandName} Instagram Growth{' '}
            <span className="plixy-gradient-text">
              Pricing &amp; Plans
            </span>
          </h2>

          <p className="mt-4 text-lg text-slate-600">
            Your own Instagram team. Guaranteed results or it's free.
            <br />
            One-time service. Results start within 1 day of setup.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">

          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-3xl p-8 md:p-10 bg-white border ${
                plan.highlight
                  ? 'border-transparent shadow-2xl'
                  : 'border-slate-200'
              } transition-all`}
            >

              {plan.highlight && (
                <>
                  <div className="absolute inset-0 rounded-3xl plixy-gradient-ring opacity-10 pointer-events-none" />

                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white plixy-gradient-ring inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    MOST POPULAR
                  </div>
                </>
              )}

              <h3 className="text-2xl font-extrabold text-[#0a1930]">
                {plan.name}
              </h3>

              <p className="mt-1 text-sm text-slate-500">
                {plan.tagline}
              </p>

              <div className="mt-6 flex items-baseline gap-1">
                <span className="text-5xl font-extrabold text-[#0a1930]">
                  ${plan.price}
                </span>
              </div>

              <Link
                to={`/get-started?plan=${plan.id}&platform=instagram&price=${plan.price}&name=${encodeURIComponent(
                  plan.name
                )}&range=${encodeURIComponent(plan.range)}`}
                className={`mt-6 w-full inline-flex items-center justify-center gap-1 py-3.5 rounded-full font-semibold text-base ${
                  plan.highlight
                    ? 'plixy-dark-btn plixy-gradient-border'
                    : 'plixy-dark-btn'
                }`}
              >
                Get Started
                <ChevronRight className="w-5 h-5" />
              </Link>

              <ul className="mt-6 space-y-3">
                {plan.features.map((f, i) => (
                  <li
                    key={f}
                    className="flex items-start gap-2.5"
                  >
                    <span className="w-5 h-5 shrink-0 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                      <Check
                        className="w-3 h-3 text-emerald-600"
                        strokeWidth={3}
                      />
                    </span>

                    <span
                      className={`${
                        i === 0
                          ? 'font-bold underline decoration-slate-300 underline-offset-4'
                          : 'font-semibold'
                      } text-[#0a1930]`}
                    >
                      {f === 'AI-Powered Growth Engine' ? (
                        <span className="plixy-gradient-text">
                          {f}
                        </span>
                      ) : (
                        f
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <TrustBar offset={plan.highlight ? 2 : 0} />

            </div>
          ))}

        </div>

        <p className="mt-10 text-center text-sm text-slate-500">
          All prices in USD. Cancel any time. Guaranteed results or your money back.
        </p>

      </div>
    </section>
  );
};

export default Pricing;
